import { useState } from 'react';
import { apiFetch } from './streamApi';

/**
 * Two-way audio ("Speak") — shared by the mobile dashboard's Play sheet and the
 * industrial Ops console's incident response panel.
 *
 * Only the logic is shared; each screen renders its own buttons in its own theme.
 * The hard part is the capture path, which has three cases:
 *   • Flutter WebView → hand recording to the native shell (window.FlutterAudio),
 *     because getUserMedia in the Android WebView needs permissions the shell owns.
 *   • Browser        → MediaRecorder, uploaded as multipart to /controls/speak/audio.
 *   • No mic at all  → type text, spoken aloud server-side via /controls/speak.
 * Mic failures are reported as `micError` text rather than thrown, since the text
 * box is always a working fallback.
 */
export default function useSpeak() {
  const [speakText, setSpeakText] = useState('');
  const [speaking,  setSpeaking]  = useState(false);
  const [recording, setRecording] = useState(false);
  const [recorder,  setRecorder]  = useState(null);
  const [micError,  setMicError]  = useState('');

  const speakTyped = async (text) => {
    const t = (text ?? speakText).trim();
    setSpeaking(true);
    try {
      await apiFetch('/controls/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t || null }),
      });
      return true;
    } catch { return false; }
    finally { setSpeaking(false); }
  };

  const toggleRecord = async () => {
    if (recording) {
      if (recorder) recorder.stop();
      else if (window.FlutterAudio) window.FlutterAudio.postMessage('stop');
      return;
    }
    setMicError('');
    // Speak here is a broadcast — clear any per-device target a prior Audio Devices
    // "Talk" left set, so this plays on the default speaker.
    window.__speakTarget = null;

    // Prefer native recording when running inside the Flutter WebView app.
    if (window.FlutterAudio) {
      window.__flutterAudioDone = (status) => {
        setRecording(false);
        setSpeaking(false);
        if (status === 'permission_error') setMicError('Mic access denied. Go to Android Settings → Apps → Parvekshak → Permissions → Microphone to allow.');
        else if (status === 'record_error') setMicError('Recording failed. Please try again.');
        window.__flutterAudioDone = null;
      };
      window.FlutterAudio.postMessage('start');
      setRecording(true);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError('Mic not supported in this browser. Use the text box to speak.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size === 0) { setMicError('No audio recorded. Check mic permissions.'); return; }
        const fd = new FormData();
        fd.append('audio', blob, 'voice.webm');
        setSpeaking(true);
        try {
          await apiFetch('/controls/speak/audio', { method: 'POST', body: fd });
        } catch {
          setMicError('Failed to send audio. Check server connection.');
        } finally { setSpeaking(false); }
      };
      setRecorder(mr);
      setRecording(true);
      mr.start();
    } catch {
      const isHttp = window.location.protocol === 'http:' && window.location.hostname !== 'localhost';
      const isFlutterApp = !!(window.FlutterLog || window.FlutterAudio);
      setMicError(isHttp
        ? 'Mic requires HTTPS. Use the text box to speak.'
        : isFlutterApp
          ? 'Mic access denied. Go to Android Settings → Apps → Parvekshak → Permissions → Microphone to allow.'
          : 'Mic access denied. Allow microphone in browser settings.');
    }
  };

  return { speakText, setSpeakText, speaking, recording, micError, setMicError, toggleRecord, speakTyped };
}
